import { addSeconds } from 'date-fns';
import { CheckCircle2, PanelLeftOpenIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTimer } from 'react-timer-hook';

import QuizTimer from '@/components/features/quiz/header/QuizTimer';
import SectionContent from '@/components/features/quiz/question/question-resolver/SectionContent';
import BottomNavigation from '@/components/features/quiz/quiz-resolver/BottomNavigation';
import QuizExpirationTimeDialog from '@/components/features/quiz/quiz-resolver/QuizExpirationTimeDialog';
import QuizNavigationBlockerDialog from '@/components/features/quiz/quiz-resolver/QuizNavigationBlockerDialog';
import QuizSectionsNavigationList from '@/components/features/quiz/quiz-resolver/QuizSectionsNavigationList';
import { Button } from '@/components/ui/button';
import { Loading } from '@/components/ui/loading';
import { ScrollProvider } from '@/components/ui/scroll';
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useBeforeUnload } from '@/hooks/before-unload';
import { useQuizNavigation, useQuizQuickSave, useQuizResolverData, useSectionTimers } from '@/hooks/quiz';
import { useQuizResolverStore } from '@/store/quiz-store';

export default function QuizPage() {
  useBeforeUnload(true);
  const [isTimeExpiredDialogOpen, setIsTimeExpiredDialogOpen] = useState<boolean>(false);
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);
  const [sectionTimers, setSectionTimers] = useState<(number | null)[]>([]);
  const { freeNavigation, instantMark, data, isSuccess, sectionIndex, isPending, isError, totalQuestions, totalSections, isQuizTimer, isSectionTimer, currentSection, localTimestamp } =
    useQuizResolverData();
  const { quickSave } = useQuizQuickSave();
  const getExportedAnswers = useQuizResolverStore((state) => state.getExportedAnswers);

  const { totalSeconds: quizTimerRemaining, restart: restartQuizTimer } = useTimer({
    expiryTimestamp: new Date(),
    onExpire: () => setIsTimeExpiredDialogOpen(true),
    autoStart: false,
  });

  const {
    state: navigationState,
    actions: navigationActions,
    scrollContainer,
  } = useQuizNavigation({
    freeNavigation: freeNavigation,
    instantMark: instantMark,
    sectionTimers: sectionTimers,
    onForceSubmit: () => setIsTimeExpiredDialogOpen(true),
  });

  const { totalSeconds: sectionTimerRemaining } = useSectionTimers({
    quiz: data?.quiz,
    attempt: data?.attempt,
    isSuccess: isSuccess,
    currentSection: sectionIndex,
    sectionTimers,
    setSectionTimers,
    onNavigateNext: navigationActions.onNavigateAvailable,
    onForceSubmit: () => setIsTimeExpiredDialogOpen(true),
  });

  const isSectionMarked: boolean = Boolean(currentSection && data?.attempt.body.metadata.sections.some((s) => s.id === sectionIndex));

  const handleNavigateItem = (sectionIndex: number) => {
    if (freeNavigation) {
      navigationActions.onNavigate(sectionIndex);
    }
    setIsSheetOpen(false);
  };

  useEffect(() => {
    if (data?.quiz.time) {
      restartQuizTimer(addSeconds(new Date(), data.quiz.time), true);
    }
  }, [isSuccess]);

  useEffect(() => {
    const answers = getExportedAnswers();
    if (!localTimestamp || answers.length === 0) return;
    quickSave();
  }, [localTimestamp, getExportedAnswers]);

  return (
    <ScrollProvider
      options={{
        scrollIntoViewOptions: { behavior: 'smooth' },
        threshold: 0.01,
      }}
    >
      <div className={'base-direction-col'}>
        <QuizExpirationTimeDialog
          open={isTimeExpiredDialogOpen}
          onOpenChange={setIsTimeExpiredDialogOpen}
          isPending={navigationState.isPending || navigationState.isFinishing}
          onSubmit={async () => {
            await navigationActions.onSubmit();
          }}
        />
        <QuizNavigationBlockerDialog enabled={!isError && !navigationState.isFinishing} />
        <div className="bg-background viewport-height relative flex w-full flex-col rounded-2xl border">
          <div
            data-pending={isPending || navigationState.isPending}
            className={'bg-surface-primary/35 absolute inset-0 z-20 flex items-center justify-center rounded-2xl backdrop-blur-xs data-[pending=false]:hidden'}
          >
            <Loading />
          </div>

          <div className="items-center space-y-3 border-b p-4">
            <div className="grid auto-rows-auto grid-cols-[auto_1fr] items-center gap-3 md:grid-cols-[1fr_auto]">
              {/* Mobile menu button */}
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="md:hidden">
                    <PanelLeftOpenIcon />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetBody>
                    <SheetHeader>
                      <SheetTitle>Quiz Sections</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 p-3">
                      <QuizSectionsNavigationList quiz={data?.quiz} totalQuestions={totalQuestions} onNavigate={handleNavigateItem} />
                    </div>
                  </SheetBody>
                </SheetContent>
              </Sheet>

              {/* Timers - mobile: bottom, desktop: left */}
              <div className="order-3 col-span-full space-y-2 md:order-1 md:col-span-1">
                {isQuizTimer && <QuizTimer isSectionTimer={false} remainingTime={quizTimerRemaining} />}
                {isSectionTimer && sectionTimerRemaining && <QuizTimer isSectionTimer={true} remainingTime={sectionTimerRemaining} />}
              </div>

              <Button
                size="sm"
                variant={'tertiary'}
                className={'order-2 justify-self-end'}
                onClick={navigationActions.onSaveAndSubmit}
                disabled={isPending || navigationState.isPending || navigationState.isFinishing}
              >
                <CheckCircle2 />
                Zapisz i zakończ
              </Button>
            </div>
          </div>
          <div className="flex h-full w-full grow divide-x overflow-hidden">
            {/* Desktop sidebar - hidden on mobile */}
            <div className="hidden w-3xs overflow-y-auto p-3 md:block md:p-4">
              <QuizSectionsNavigationList quiz={data?.quiz} totalQuestions={totalQuestions} onNavigate={handleNavigateItem} />
            </div>
            <div ref={scrollContainer} className={'flex w-full flex-col divide-y'}>
              <SectionContent sectionIndex={sectionIndex} section={currentSection} showMarked={isSectionMarked} />
              <BottomNavigation
                currentSection={sectionIndex}
                totalSections={totalSections}
                freeNavigation={freeNavigation}
                instantMark={instantMark}
                state={navigationState}
                showMarked={isSectionMarked}
                actions={navigationActions}
              />
            </div>
          </div>
        </div>
      </div>
    </ScrollProvider>
  );
}
